import { getUserContext } from "./userContext";

export const LocalStorageKeys = Object.freeze({
  DARK_MODE: "dark-mode",
  AGENT_VIEW_PREFERENCES: "agent-view-preferences",
  PLAYGROUND_CREDENTIALS: "playground-credentials",
  // Session-scoped, never localStorage: holds the Playground M2M client
  // secret, which must not be left in plaintext at rest.
  PLAYGROUND_M2M_SECRET: "playground-m2m-secret",
  Filters: {
    AGENT_HISTORY_MESSAGES: "agent-history-messages",
    API_KEYS: "api-keys",
    API_REQUESTS: "api-requests",
    CHALK_LOGS: "chalk-logs",
    EVENT_STREAM: "event-stream",
    MODEL_USAGE: "model-usage",
    MODEL_USAGE_BREAKDOWN: "model-usage-breakdown",
    ORGANIZATION_API_KEYS: "organization-api-keys",
    ORGANIZATION_PROJECTS: "organization-projects",
    PROJECTS: "projects",
    SESSIONS: "sessions",
    USERS: "users",
  },
});

export type Storage<T> = {
  setItem: (persistenceKey: string, value: T | null) => void;
  getItem: (persistenceKey: string) => T | null;
};

export const getStorageKey = (persistenceKey: string) => {
  const userContext = getUserContext();
  return userContext.organization
    ? `${userContext.organization}_${userContext.userId}_${persistenceKey}`
    : `${userContext.userId}_${persistenceKey}`;
};

export const useLocalStorage = <T>(
  options: {
    globalStorage?: boolean;
    // Use window.sessionStorage instead of window.localStorage: same
    // org/user key scoping, but the value is dropped when the tab
    // closes and is never written to disk. For short-lived secrets that
    // still need to survive a component remount or a reload within the
    // tab, where localStorage would leave plaintext at rest.
    sessionStorage?: boolean;
  } = {
    globalStorage: false,
  }
): Storage<T> => {
  const { globalStorage, sessionStorage } = options;
  const backing = () =>
    sessionStorage ? window.sessionStorage : window.localStorage;
  return {
    setItem: (persistenceKey: string, value: T | null) => {
      backing().setItem(
        globalStorage ? persistenceKey : getStorageKey(persistenceKey),
        JSON.stringify(value)
      );
    },
    getItem: (persistenceKey: string) => {
      const storedValue = backing().getItem(
        globalStorage ? persistenceKey : getStorageKey(persistenceKey)
      );
      return storedValue ? (JSON.parse(storedValue) as T) : null;
    },
  };
};
